<script setup>
import axios from 'axios';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import { t } from '../i18n';
import { readUiState, writeUiState } from '../uiState';
import NewsPostList from './NewsPostList.vue';
import NewsTagPicker from './NewsTagPicker.vue';

// News feed (docs/news/SPEC.md, decisions 13–15, 20–23): newest first, "only my securities" on by default,
// a tag filter with and/or, pages of 30 loaded while scrolling. The filter is remembered in the browser.
const props = defineProps({
  desktop: {
    type: Boolean,
    default: false,
  },
  // Bumped by pull to refresh: the feed is read anew.
  dataVersion: {
    type: Number,
    default: 0,
  },
});

function readFilter() {
  const saved = readUiState('news.filter', null);
  return {
    mine: typeof saved?.mine === 'boolean' ? saved.mine : true,
    tags: Array.isArray(saved?.tags) ? saved.tags.filter((tag) => typeof tag === 'string').slice(0, 50) : [],
    mode: saved?.mode === 'and' ? 'and' : 'or',
  };
}

const filter = ref(readFilter());
const mine = computed({
  get: () => filter.value.mine,
  set: (value) => { filter.value = { ...filter.value, mine: value }; },
});

// Tag catalog for the picker and the synonyms of post chips (GET /news/tags), read once.
const catalog = ref(null);
const catalogLoading = ref(false);
const pickerOpen = ref(false);

async function loadCatalog() {
  if (catalog.value || catalogLoading.value) {
    return;
  }
  catalogLoading.value = true;
  try {
    const { data } = await axios.get('/news/tags');
    catalog.value = data;
  } catch {
    // The picker shows nothing; chips still filter by their own tag
  } finally {
    catalogLoading.value = false;
  }
}

function openPicker() {
  pickerOpen.value = true;
  loadCatalog();
}

function toggleTag(tag) {
  const tags = filter.value.tags.includes(tag) ? filter.value.tags.filter((x) => x !== tag) : [...filter.value.tags, tag];
  filter.value = { ...filter.value, tags };
}

// A chip of a post adds its tag (its main form when it is a synonym) to the filter.
function toggleChip(label) {
  const raw = label.replace(/^#/, '').toLowerCase();
  toggleTag(catalog.value?.aliases?.[raw] ?? raw);
}

function tagLabel(tag) {
  const ticker = catalog.value?.categories?.find((c) => c.key === 'ticker')?.tags.some((x) => x.tag === tag);
  return `#${ticker ? tag.toUpperCase() : tag}`;
}

function clearFilter() {
  filter.value = { ...filter.value, tags: [] };
}

const items = ref([]);
const nextCursor = ref(null);
const hasTickers = ref(true);
const loading = ref(true);
const reloading = ref(false);
const error = ref('');
const loadingMore = ref(false);
const moreError = ref('');
let request = 0;

function params(cursor = null) {
  return {
    mine: filter.value.mine ? 1 : 0,
    ...(filter.value.tags.length > 0 ? { tags: filter.value.tags, mode: filter.value.mode } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

async function loadFirst() {
  const id = ++request;
  moreError.value = '';
  loading.value = items.value.length === 0;
  reloading.value = !loading.value;
  error.value = '';
  try {
    const { data } = await axios.get('/news/list', { params: params() });
    if (id !== request) {
      return;
    }
    items.value = data.items;
    nextCursor.value = data.next_cursor;
    hasTickers.value = data.has_tickers;
  } catch (e) {
    if (id === request && e?.response?.status !== 401) {
      error.value = extractApiError(e, t('news.loadFailed'));
    }
  } finally {
    if (id === request) {
      loading.value = false;
      reloading.value = false;
    }
  }
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) {
    return;
  }
  const id = request;
  loadingMore.value = true;
  moreError.value = '';
  try {
    const { data } = await axios.get('/news/list', { params: params(nextCursor.value) });
    if (id !== request) {
      return;
    }
    items.value = [...items.value, ...data.items];
    nextCursor.value = data.next_cursor;
  } catch (e) {
    if (id === request && e?.response?.status !== 401) {
      moreError.value = t('news.loadMoreFailed');
    }
  } finally {
    loadingMore.value = false;
  }
}

watch(filter, (value) => {
  writeUiState('news.filter', value);
  loadFirst();
});
watch(() => props.dataVersion, loadFirst);

// Next page before the end is reached (rootMargin 600px); the button stays for the keyboard and reduced motion.
const sentinel = ref(null);
let observer = null;

function observe() {
  observer?.disconnect();
  if (sentinel.value && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        loadMore();
      }
    }, { rootMargin: '600px' });
    observer.observe(sentinel.value);
  }
}

watch(sentinel, () => nextTick(observe));

onMounted(() => {
  loadFirst();
  loadCatalog();
});
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <div class="space-y-3 sm:space-y-4">
    <div>
      <h1 class="px-1 pt-1 text-[1.75rem] font-bold leading-8 text-slate-100 sm:px-0 sm:pt-0 sm:text-2xl sm:font-semibold">
        {{ t('news.title') }}
      </h1>
      <p class="mt-1 px-1 text-[0.8125rem] text-slate-500 sm:px-0">
        {{ t('news.source') }}
      </p>
    </div>

    <!-- Filter: sticky on a phone, like the operations strip -->
    <div class="sticky top-[calc(2.75rem+env(safe-area-inset-top))] z-30 -mx-4 bg-slate-950/85 px-4 py-2 backdrop-blur-md sm:static sm:mx-0 sm:max-w-3xl sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
      <div class="flex items-center justify-between gap-3">
        <label class="flex min-h-11 cursor-pointer items-center gap-3 text-[0.9375rem] text-slate-100 sm:min-h-0 sm:text-sm">
          <input
            v-model="mine"
            type="checkbox"
            class="size-5 rounded border-slate-600 bg-slate-800 accent-slate-100 sm:size-4"
          >
          {{ t('news.onlyMine') }}
        </label>
        <button
          type="button"
          class="flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[0.875rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          :class="filter.tags.length > 0 ? 'bg-slate-100 text-slate-950' : 'bg-slate-800 text-slate-300'"
          aria-haspopup="dialog"
          @click="openPicker"
        >
          <svg
            class="size-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {{ t('news.filter') }}<template v-if="filter.tags.length > 0">
            · {{ filter.tags.length }}
          </template>
        </button>
      </div>

      <!-- Chosen tags (decisions 20, 21): tap to remove; and/or when there are two or more -->
      <div
        v-if="filter.tags.length > 0"
        class="mt-2 flex flex-wrap items-center gap-1.5"
      >
        <div
          v-if="filter.tags.length > 1"
          class="flex rounded-full bg-slate-800 p-0.5"
          role="group"
          :aria-label="t('news.modeLabel')"
        >
          <button
            v-for="mode in ['or', 'and']"
            :key="mode"
            type="button"
            class="h-7 rounded-full px-3 text-[0.8125rem] font-medium"
            :class="filter.mode === mode ? 'bg-slate-100 text-slate-950' : 'text-slate-400'"
            :aria-pressed="filter.mode === mode"
            @click="filter = { ...filter, mode }"
          >
            {{ t(`news.mode.${mode}`) }}
          </button>
        </div>
        <button
          v-for="tag in filter.tags"
          :key="tag"
          type="button"
          class="flex h-7 items-center gap-1 rounded-full bg-slate-100 pl-2.5 pr-2 text-[0.8125rem] font-medium text-slate-950"
          :aria-label="t('news.removeTag', { tag: tagLabel(tag) })"
          @click="toggleTag(tag)"
        >
          {{ tagLabel(tag) }}
          <svg
            class="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <button
          type="button"
          class="h-7 px-1.5 text-[0.8125rem] font-medium text-slate-400 hover:text-slate-200"
          @click="clearFilter"
        >
          {{ t('news.clearFilter') }}
        </button>
      </div>
    </div>

    <NewsTagPicker
      :open="pickerOpen"
      :categories="catalog?.categories ?? []"
      :loading="catalogLoading"
      :selected="filter.tags"
      @close="pickerOpen = false"
      @toggle="toggleTag"
    />

    <div
      v-if="loading"
      class="space-y-2 rounded-3xl bg-slate-900 p-5 sm:max-w-3xl sm:rounded-lg"
    >
      <span class="sr-only">{{ t('common.loading') }}</span>
      <div
        v-for="n in 4"
        :key="n"
        class="h-20 rounded-xl bg-slate-800 motion-safe:animate-pulse"
      />
    </div>

    <div
      v-else-if="error"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:max-w-3xl sm:rounded-lg"
    >
      <p class="text-[0.9375rem] text-rose-300">
        {{ error }}
      </p>
      <button
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        @click="loadFirst"
      >
        {{ t('common.retry') }}
      </button>
    </div>

    <div
      v-else-if="items.length === 0"
      class="rounded-3xl bg-slate-900 p-6 text-center sm:max-w-3xl sm:rounded-lg"
    >
      <p class="text-[0.9375rem] text-slate-300">
        <template v-if="filter.tags.length > 0">
          {{ t('news.emptyFilter') }}
        </template>
        <template v-else>
          {{ mine ? (hasTickers ? t('news.emptyMine') : t('news.noTickers')) : t('news.empty') }}
        </template>
      </p>
      <button
        v-if="filter.tags.length > 0"
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        @click="clearFilter"
      >
        {{ t('news.clearFilter') }}
      </button>
      <button
        v-else-if="mine"
        type="button"
        class="mt-4 h-11 w-full rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 sm:h-9 sm:w-auto sm:rounded-md sm:px-4 sm:text-sm"
        @click="mine = false"
      >
        {{ t('news.showAll') }}
      </button>
    </div>

    <template v-else>
      <NewsPostList
        class="sm:max-w-3xl"
        :class="reloading ? 'opacity-50' : ''"
        :aria-busy="loadingMore || reloading"
        :items="items"
        :desktop="desktop"
        chips-clickable
        @chip="toggleChip"
      />

      <div
        v-if="nextCursor"
        ref="sentinel"
        class="text-center"
      >
        <p
          v-if="moreError"
          class="mb-2 text-[0.8125rem] text-rose-300"
        >
          {{ moreError }}
        </p>
        <button
          type="button"
          class="h-11 rounded-2xl px-4 text-[0.9375rem] font-medium text-slate-300 disabled:opacity-50 sm:h-9 sm:text-sm"
          :disabled="loadingMore"
          @click="loadMore"
        >
          {{ loadingMore ? t('common.loading') : t('news.loadMore') }}
        </button>
      </div>
      <p
        v-else
        class="pb-2 text-center text-[0.8125rem] text-slate-500"
      >
        {{ t('news.end') }}
      </p>
    </template>
  </div>
</template>
